import classnames from 'classnames';
import React, { FC, useCallback, useEffect } from 'react';
import { Picture, createPictureFactory, useAnalytics } from '@funtech-int/yango-shared';

import { MovieAnnouncement } from '@/scenarios/profiles/lib/MovieAnnouncementTypes';

import { createAnalyticsEvents } from '../../lib/createAnalyticsEvents';

import styles from './MovieAnnouncementBackground.module.css';

const backgroundPictureFactory = createPictureFactory({
    fallback: {
        src: '2352x1323',
    },
});

const logoPictureFactory = createPictureFactory({
    fallback: {
        srcSet: {
            '1x': '450x136',
            '2x': '900x272',
        },
        src: '450x136',
    },
});

interface MovieAnnouncementBackgroundProps {
    movie: MovieAnnouncement;
    isActive?: boolean;
    isLooped?: boolean;
}

export const MovieAnnouncementBackground: FC<MovieAnnouncementBackgroundProps> = ({
    movie,
    isActive,
    isLooped,
}) => {
    const analytics = useAnalytics(createAnalyticsEvents);

    const handleShowed = useCallback(
        () => analytics.movieImpressionShowed(movie.contentId),
        [analytics, movie.contentId]
    );

    const { img: backgroundImg } = backgroundPictureFactory({
        fallbackUrl: movie.coverUrl,
    });

    const { img: logoImg } = logoPictureFactory({
        fallbackUrl: movie.logoUrl,
    });

    useEffect(() => {
        if (isActive || isLooped) {
            handleShowed();
        }
    }, [isActive, isLooped, handleShowed]);

    return (
        <div
            className={classnames(styles.movie, {
                [styles.movieActive]: isActive,
                [styles.movieInfiniteAnimation]: isLooped,
            })}
            aria-hidden
        >
            <div className={styles.background}>
                <Picture
                    className={styles.cover}
                    alt=""
                    loading="lazy"
                    srcSet={backgroundImg.srcSet}
                    src={backgroundImg.src}
                    data-tid="AdaptiveImage"
                />
            </div>
            <div className={styles.titleLogoBlock}>
                <Picture
                    className={styles.titleLogo}
                    alt=""
                    loading="lazy"
                    srcSet={logoImg.srcSet}
                    src={logoImg.src}
                    data-tid="AdaptiveImage"
                />
                {movie.text && <div className={styles.text}>{movie.text}</div>}
            </div>
        </div>
    );
};
